import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import { isPriceVariationsEnabled, requireListingImage } from '../../../util/configHelpers';

import { AspectRatioWrapper, ResponsiveImage, ListingCardThumbnail } from '../../../components';
import {
  getInfoCardOverlayStyle,
  listingsShareAuthor,
} from '../SearchMap/SearchMap.helpers';

import css from './SearchMapInfoCard.module.css';

const getPriceMessage = (listing, intl, config) => {
  const { price, publicData } = listing.attributes;
  const formattedPrice =
    price && price.currency === config.currency
      ? formatMoney(intl, price)
      : price?.currency
      ? price.currency
      : null;

  if (!formattedPrice) {
    return '';
  }

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === publicData?.listingType
  );
  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const pricePerUnit = intl.formatMessage(
    { id: 'SearchMapInfoCard.perUnit' },
    { unitType: publicData?.unitType }
  );

  return hasMultiplePriceVariants
    ? intl.formatMessage(
        { id: 'SearchMapInfoCard.priceStartingFrom' },
        { priceValue: formattedPrice, pricePerUnit }
      )
    : intl.formatMessage(
        { id: 'SearchMapInfoCard.price' },
        { priceValue: formattedPrice, pricePerUnit }
      );
};

// ListingCard is the listing info without overlayview or carousel controls
const ListingCard = props => {
  const { className, clickHandler, intl, isInCarousel, listing, urlToListing, config } = props;

  const { title, publicData } = listing.attributes;
  const { cardStyle } = publicData || {};
  const priceMessage = getPriceMessage(listing, intl, config);
  const firstImage = listing.images && listing.images.length > 0 ? listing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === publicData?.listingType
  );
  const showListingImage = requireListingImage(foundListingTypeConfig);

  // listing card anchor needs sometimes inherited border radius.
  const classes = classNames(
    css.anchor,
    css.borderRadiusInheritTop,
    { [css.borderRadiusInheritBottom]: !isInCarousel },
    className
  );

  return (
    <a
      alt={title}
      className={classes}
      href={urlToListing}
      onClick={e => {
        e.preventDefault();
        // Use clickHandler from props to call internal router
        clickHandler(listing);
      }}
    >
      <div
        className={classNames(css.card, css.borderRadiusInheritTop, {
          [css.borderRadiusInheritBottom]: !isInCarousel,
        })}
      >
        {showListingImage ? (
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
          >
            <ResponsiveImage
              rootClassName={classNames(css.rootForImage, css.borderRadiusInheritTop)}
              alt={title}
              noImageMessage={intl.formatMessage({ id: 'SearchMapInfoCard.noImage' })}
              image={firstImage}
              variants={variants}
              sizes="250px"
            />
          </AspectRatioWrapper>
        ) : (
          <ListingCardThumbnail
            style={cardStyle}
            listingTitle={title}
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
          />
        )}
        <div className={classNames(css.info, { [css.borderRadiusInheritBottom]: !isInCarousel })}>
          <div className={classNames(css.price, { [css.noPriceSetLabel]: !priceMessage })}>
            {priceMessage}
          </div>
          <div className={css.name}>{title}</div>
        </div>
      </div>
    </a>
  );
};

const ArtistListingsCard = props => {
  const {
    listings,
    createURLToListing,
    onListingInfoCardClicked,
    intl,
    config,
  } = props;
  const firstListing = ensureListing(listings[0]);
  const artistName =
    firstListing.author?.attributes?.profile?.displayName || firstListing.attributes.title;
  const serviceCountLabel = `${listings.length} ${listings.length === 1 ? 'service' : 'services'}`;
  const variantPrefix = config.layout.listingImage?.variantPrefix || 'listing-card';

  return (
    <div className={css.artistCard}>
      <div className={css.artistHeaderText}>
        <div className={css.artistName}>{artistName}</div>
        <div className={css.serviceCount}>{serviceCountLabel}</div>
      </div>

      <div className={css.servicesGrid}>
        {listings.map(listingItem => {
          const listing = ensureListing(listingItem);
          const { title, publicData } = listing.attributes;
          const priceMessage = getPriceMessage(listing, intl, config);
          const urlToListing = createURLToListing(listing);
          const firstImage = listing.images?.[0] || null;
          const imageVariants = firstImage
            ? Object.keys(firstImage?.attributes?.variants || {}).filter(k =>
                k.startsWith(variantPrefix)
              )
            : [];
          const { cardStyle } = publicData || {};

          return (
            <a
              key={listing.id.uuid}
              className={css.serviceTile}
              href={urlToListing}
              onClick={e => {
                e.preventDefault();
                onListingInfoCardClicked(listing);
              }}
            >
              <div className={css.serviceLeft}>
                <div className={css.serviceThumb}>
                  {firstImage ? (
                    <ResponsiveImage
                      rootClassName={css.serviceThumbImage}
                      alt={title}
                      noImageMessage={intl.formatMessage({ id: 'SearchMapInfoCard.noImage' })}
                      image={firstImage}
                      variants={imageVariants}
                      sizes="52px"
                    />
                  ) : (
                    <ListingCardThumbnail
                      style={cardStyle}
                      listingTitle={title}
                      className={css.serviceThumbFallback}
                      width={1}
                      height={1}
                    />
                  )}
                </div>
                <div className={css.serviceText}>
                  <div className={css.serviceTitle}>{title}</div>
                  {priceMessage ? <div className={css.servicePrice}>{priceMessage}</div> : null}
                </div>
              </div>
              <span className={css.serviceArrow} aria-hidden="true">
                ›
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

/**
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {Function} props.onListingInfoCardClicked - The function to handle the listing info card click
 * @param {Function} props.createURLToListing - The function to create the URL to the listing
 * @param {Object} props.config - The configuration
 * @returns {JSX.Element}
 */
const SearchMapInfoCard = props => {
  const [currentListingIndex, setCurrentListingIndex] = useState(0);
  const intl = useIntl();
  const {
    className,
    rootClassName,
    listings,
    createURLToListing,
    onListingInfoCardClicked,
    config,
  } = props;
  const currentListing = ensureListing(listings[currentListingIndex]);
  const hasCarousel = listings.length > 1;
  const isSingleArtistGroup = listingsShareAuthor(listings);

  const classes = classNames(rootClassName || css.root, className);
  const overlayStyle = getInfoCardOverlayStyle();

  if (isSingleArtistGroup) {
    return (
      <div className={classes} style={overlayStyle}>
        <div className={css.caretShadow} />
        <ArtistListingsCard
          listings={listings}
          createURLToListing={createURLToListing}
          onListingInfoCardClicked={onListingInfoCardClicked}
          intl={intl}
          config={config}
        />
        <div className={css.caret} />
      </div>
    );
  }

  const caretClass = classNames(css.caret, { [css.caretWithCarousel]: hasCarousel });

  return (
    <div className={classes} style={overlayStyle}>
      <div className={css.caretShadow} />
      <ListingCard
        clickHandler={onListingInfoCardClicked}
        urlToListing={createURLToListing(currentListing)}
        listing={currentListing}
        intl={intl}
        isInCarousel={hasCarousel}
        config={config}
      />
      {hasCarousel ? (
        <div className={classNames(css.paginationInfo, css.borderRadiusInheritBottom)}>
          <button
            className={css.paginationPrev}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length - 1) % listings.length
              );
            }}
          />
          <div className={css.paginationPage}>
            {`${currentListingIndex + 1}/${listings.length}`}
          </div>
          <button
            className={css.paginationNext}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentListingIndex(
                prevListingIndex => (prevListingIndex + listings.length + 1) % listings.length
              );
            }}
          />
        </div>
      ) : null}
      <div className={caretClass} />
    </div>
  );
};

export default SearchMapInfoCard;
